import { useEffect, useCallback, useMemo, useRef } from 'react';
import { getDbQueryResult, setDbQueryResult } from '../../store/dbQueryResultCache';
import { isOpaqueCell } from '../dbCellTags';
import { MAX_RENDER_ROWS, groupPendingRows, cellKey } from '../dbGridModel';
import { findMatches, rowsAsShown, firstMatchAtOrAfterCell, stepMatch } from '../dbGridFind';
import useDbGridKeyboardNav from './useDbGridKeyboardNav';
import { isRevertToOriginal } from './dbResultsTabHelpers';

/** Row and cell selection, find in results, cell writes and the grid cursor. */
export function useDbSelectionFind({
  tab,
  result,
  isQueryTab,
  error,
  loading,
  pendingRows,
  setPendingRows,
  pendingEdits,
  setPendingEdits,
  primaryKeyCols,
  editingCell,
  tableColumns,
  uniqueCols,
  sortState,
  allLoaded,
  totalRows,
  selectedRows,
  setSelectedRows,
  selectedCells,
  setSelectedCells,
  selectionAnchorRef,
  cellAnchorRef,
  setCellAnchor,
  transposed,
  findOpen,
  setFindOpen,
  findQuery,
  setFindDraft,
  setFindQuery,
  findMatchCase,
  findIndex,
  setFindIndex,
  contextMenu,
  setContextMenu,
  scrollTopRef,
  resultFilterRef,
  typeIntoCell,
  typeIntoPendingCell,
  handleCellOpen,
  drawnColIndexes,
  viewStateRef,
}) {
  // Close the context menu on click-away, Escape, scroll, or window blur.
  useEffect(() => {
    if (!contextMenu) return undefined;
    const close = () => setContextMenu(null);
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      // Swallow the event: this listener is capture-phase, and the component's
      // own bubble-phase Escape handler would go on to discard every staged
      // edit. Dismissing a menu must never destroy pending work.
      e.preventDefault();
      e.stopImmediatePropagation();
      close();
    };
    window.addEventListener('mousedown', close);
    window.addEventListener('blur', close);
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('scroll', close, true);
    return () => {
      window.removeEventListener('mousedown', close);
      window.removeEventListener('blur', close);
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('scroll', close, true);
    };
  }, [contextMenu, setContextMenu]);

  // Explorer-style row selection: plain click replaces the selection with
  // just that row (clicking the lone selected row again clears it — the only
  // way to deselect without a modifier), Ctrl/Cmd+click toggles a row into
  // the existing multi-selection, Shift+click extends a contiguous range
  // from the last clicked/anchor row. The anchor tracks whatever row a plain
  // or ctrl click last touched so a later Shift+click has somewhere to range
  // from. Declared with the selection state above rather than here, because it
  // is read during render (viewStateRef) and every other reader is a callback.
  const toggleRowSelected = useCallback((rowIndex, e) => {
    const ctrl = !!(e && (e.ctrlKey || e.metaKey));
    const shift = !!(e && e.shiftKey);
    if (shift && selectionAnchorRef.current != null) {
      const lo = Math.min(selectionAnchorRef.current, rowIndex);
      const hi = Math.max(selectionAnchorRef.current, rowIndex);
      const next = new Set();
      for (let r = lo; r <= hi; r++) next.add(r);
      setSelectedRows(next);
      return;
    }
    if (ctrl) {
      selectionAnchorRef.current = rowIndex;
      setSelectedRows((prev) => {
        const next = new Set(prev);
        if (next.has(rowIndex)) next.delete(rowIndex);
        else next.add(rowIndex);
        return next;
      });
      return;
    }
    selectionAnchorRef.current = rowIndex;
    setSelectedRows((prev) => (prev.size === 1 && prev.has(rowIndex) ? new Set() : new Set([rowIndex])));
    setSelectedCells(new Set());
    setCellAnchor(null);
  }, [setCellAnchor, selectionAnchorRef, setSelectedCells, setSelectedRows]);

  // Top-to-bottom order of everything the grid renders as a row, so a
  // Shift+click range can run straight across the boundary between saved rows
  // and the pending (not yet inserted) rows appended below them — which is
  // exactly the "add three rows, fill one column in all of them" case.
  const visualRows = useMemo(() => {
    // Must match what the grid DRAWS, not the order pendingRows happens to be
    // in: a duplicate renders under the row it copied, so a Shift+click range
    // that crosses it has to see it there too.
    const { under, trailing } = groupPendingRows(pendingRows);
    const out = [];
    const savedCount = result ? Math.min(result.rows.length, MAX_RENDER_ROWS) : 0;
    for (let i = 0; i < savedCount; i += 1) {
      out.push({ kind: 'r', id: i });
      under.get(i)?.forEach((r) => out.push({ kind: 'p', id: r.id }));
    }
    trailing.forEach((r) => out.push({ kind: 'p', id: r.id }));
    return out;
  }, [result, pendingRows]);

  /**
   * Mirror a table tab's state into the result cache.
   *
   * Table tabs are restored on arrival rather than refetched, so the cache has
   * to hold everything restore needs — not just the rows, but the total,
   * whether every chunk is loaded, the sort, and the key/column metadata the
   * edit path uses. Without those the tab would come back looking right and
   * behaving subtly wrong: scrolling would re-fetch chunk one, and saving
   * would have no primary key to build a WHERE from.
   */
  useEffect(() => {
    if (!tab || !tab.tableName || !result) return;
    if (resultFilterRef.current?.tabId !== tab.id) return;
    setDbQueryResult(tab.id, {
      results: [result],
      selectedIndex: 0,
      error: null,
      table: {
        filterText: resultFilterRef.current.filter,
        totalRows,
        allLoaded,
        sort: sortState,
        primaryKeyCols,
        tableColumns,
        uniqueCols,
        // Kept from the previous entry: the authoritative write happens when
        // the tab is left, since the mirror does not run on scroll or on a
        // selection change.
        scrollTop: getDbQueryResult(tab.id)?.table?.scrollTop ?? scrollTopRef.current,
        view: getDbQueryResult(tab.id)?.table?.view ?? viewStateRef.current,
      },
    });
  }, [tab, result, totalRows, allLoaded, sortState, primaryKeyCols, tableColumns, uniqueCols, resultFilterRef, scrollTopRef, viewStateRef]);

  /**
   * The rows the search may look at, when that is not simply "the first N".
   *
   * Only the record view narrows it — it draws the selected rows as its
   * columns, so those are the only rows a match can appear in there.
   *
   * Hoisted so the dependency is STABLE in the horizontal grid. `selectedRows`
   * is a fresh Set on every selection change, so reading it inline made the
   * find memo re-run on every click: the whole result set re-searched, which is
   * the exact per-keystroke cost FIND_DEBOUNCE_MS exists to avoid, paid
   * per-click instead. `null` is the same value every render.
   */
  const findRowIndexes = useMemo(
    () => (transposed ? selectedRows : null),
    [transposed, selectedRows]
  );

  // Ctrl+F matches over the rows the grid actually draws. Searching past
  // MAX_RENDER_ROWS would count matches that can never be scrolled to, so the
  // counter and the highlights would stop describing the same thing.
  const find = useMemo(() => {
    if (!findOpen || !result) return { matches: [], truncated: false, rowsSearched: 0 };
    // What the grid SHOWS: a staged edit is found by its new value, not its old.
    return findMatches(rowsAsShown(result.rows, pendingEdits), findQuery, {
      matchCase: findMatchCase,
      rowLimit: MAX_RENDER_ROWS,
      // Same rule as rowLimit, on the other axis: a hidden column's match can
      // never be highlighted or stepped onto, so it must not be counted.
      columns: drawnColIndexes,
      // And the record view narrows the ROW axis the same way — see
      // findRowIndexes. Without it, searching from the record view counted
      // matches across the whole page and highlighted none of them, which is
      // the "nothing highlights in the vertical view" case.
      rowIndexes: findRowIndexes,
    });
  }, [findOpen, result, pendingEdits, findQuery, findMatchCase, drawnColIndexes, findRowIndexes]);

  // Set of cellKeys for the highlight, built here because the key format
  // belongs to this grid rather than to the search.
  const findMatchKeys = useMemo(() => {
    const keys = new Set();
    for (const m of find.matches) keys.add(cellKey('r', m.row, m.col));
    return keys;
  }, [find.matches]);

  const activeFind = find.matches[findIndex] || null;
  const activeFindKey = activeFind ? cellKey('r', activeFind.row, activeFind.col) : null;

  // A changed query lands on the first match AT OR AFTER where you are — the
  // match you were on, else the selected cell — wrapping at the end, as VS Code,
  // browsers and Excel do. It used to keep the match NUMBER, so each extra
  // letter could jump anywhere.
  // The position is recorded only where the index is CHOSEN (here and in
  // stepFind): an effect on the active match would also fire for a new query
  // and overwrite the old position before this reads it.
  const findPosRef = useRef(null);
  useEffect(() => {
    const anchor = cellAnchorRef.current;
    const pos = findPosRef.current || (anchor?.kind === 'r' ? { row: anchor.id, col: anchor.col } : null);
    const idx = Math.max(0, firstMatchAtOrAfterCell(find.matches, pos));
    setFindIndex(idx);
    if (find.matches[idx]) findPosRef.current = { row: find.matches[idx].row, col: find.matches[idx].col };
  }, [find.matches, cellAnchorRef, setFindIndex]);

  const stepFind = useCallback(
    (delta) => {
      setFindIndex((prev) => {
        const next = stepMatch(find.matches.length, prev, delta);
        const m = find.matches[next];
        if (m) findPosRef.current = { row: m.row, col: m.col };
        return next === -1 ? 0 : next;
      });
    },
    [find.matches, setFindIndex]
  );

  const closeFind = useCallback(() => {
    setFindOpen(false);
    setFindDraft('');
    // Both, and not by letting the debounce carry it: a pending timer would
    // re-apply the old query into a closed bar.
    setFindQuery('');
    setFindIndex(0);
  }, [setFindDraft, setFindIndex, setFindOpen, setFindQuery]);

  // Bringing the active match into view now belongs to DbResultsGrid, which is
  // the only place that knows where row N is once the grid virtualizes: this
  // effect was a bare `document.querySelector('.dbm-td--find-active')` and a
  // match outside the rendered window has no such node, so the optional call
  // swallowed the miss and the viewport stopped moving while the "n of m"
  // counter kept advancing. The grid scrolls the ROW by index first and then
  // scrolls the CELL horizontally, which is what this comment's original point
  // — wide tables put the match off to the right — still requires.

  // Spreadsheet-style cell selection with the same modifier grammar as the
  // row selection above; Shift extends a rectangle (rows × columns) from the
  // anchor rather than a 1-D range.
  const toggleCellSelected = useCallback((e, kind, id, colIndex) => {
    const ctrl = !!(e && (e.ctrlKey || e.metaKey));
    const shift = !!(e && e.shiftKey);
    // Selecting a cell drops the row selection: the two are alternative modes,
    // and leaving a stale row selection behind would let Tab/Ctrl+D/Delete act
    // on rows the user no longer sees as selected. The transposed view is the
    // exception — there the row selection *is* the set of visible columns, so
    // clearing it would collapse the view out from under the click.
    if (!transposed) {
      setSelectedRows(new Set());
      selectionAnchorRef.current = null;
    }
    const anchor = cellAnchorRef.current;
    if (shift && anchor) {
      const from = visualRows.findIndex((r) => r.kind === anchor.kind && r.id === anchor.id);
      const to = visualRows.findIndex((r) => r.kind === kind && r.id === id);
      if (from !== -1 && to !== -1) {
        const [rLo, rHi] = from <= to ? [from, to] : [to, from];
        const [cLo, cHi] = anchor.col <= colIndex ? [anchor.col, colIndex] : [colIndex, anchor.col];
        const next = new Set();
        // Only columns that are actually drawn. A hidden column sitting
        // between the anchor and the click used to be swept into the
        // rectangle, so Ctrl+C copied a value that was not on screen and
        // Ctrl+V staged an edit nobody could see.
        const cols = drawnColIndexes.filter((c) => c >= cLo && c <= cHi);
        for (let r = rLo; r <= rHi; r++) {
          for (const c of cols) next.add(cellKey(visualRows[r].kind, visualRows[r].id, c));
        }
        setSelectedCells(next);
        return;
      }
    }
    const key = cellKey(kind, id, colIndex);
    setCellAnchor({ kind, id, col: colIndex });
    if (ctrl) {
      setSelectedCells((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      });
      return;
    }
    // A plain click SELECTS — it never un-selects the cell it landed on.
    //
    // This used to clear the selection when the click hit the only already
    // selected cell, which made double-click-to-edit contradict itself: the
    // first mousedown selected, the second cleared, and the dblclick then
    // opened the editor on a cell that no longer looked selected. Editing with
    // no highlight is the state the caret is least useful in, because nothing
    // says which cell the keystrokes are going to.
    //
    // Clicking a DIFFERENT cell still drops the old one — the set is replaced,
    // not extended — and Ctrl/Cmd+click above remains the way to un-select
    // deliberately, which is the modifier grammar the rest of this handler
    // already uses.
    setSelectedCells(new Set([key]));
  }, [visualRows, transposed, setCellAnchor, drawnColIndexes, cellAnchorRef, setSelectedCells, setSelectedRows, selectionAnchorRef]);

  // Applies a batch of cell writes. Saved rows and pending rows land in two
  // separate stores, but each gets exactly ONE state update for the whole
  // batch — a per-cell loop of setState calls would read stale closures and
  // silently drop all but the last write.
  const applyCellWrites = useCallback((writes) => {
    const saved = writes.filter((w) => w.kind === 'r');
    const pending = writes.filter((w) => w.kind === 'p');
    if (saved.length > 0 && result) {
      setPendingEdits((prev) => {
        const next = { ...prev };
        for (const w of saved) {
          const original = result.rows[w.id]?.[w.col];
          if (original === undefined) continue;
          // Same rule as the single-cell editor: a paste must not drop text
          // onto a binary or undecodable cell.
          if (isOpaqueCell(original)) continue;
          const key = `${w.id}:${w.col}`;
          if (isRevertToOriginal(original, w.value)) delete next[key];
          else next[key] = w.value;
        }
        return next;
      });
    }
    if (pending.length > 0) {
      const byRow = new Map();
      for (const w of pending) {
        if (!byRow.has(w.id)) byRow.set(w.id, new Map());
        byRow.get(w.id).set(w.col, w.value);
      }
      setPendingRows((prev) =>
        prev.map((r) => {
          const cols = byRow.get(r.id);
          if (!cols) return r;
          return { ...r, values: r.values.map((v, i) => (cols.has(i) ? cols.get(i) : v)) };
        })
      );
    }
  }, [result, setPendingEdits, setPendingRows]);

  // Arrows / Shift / Ctrl / Home / End / PgUp / PgDn, type-to-edit, Enter / F2,
  // Backspace clears (dbManager/useDbGridKeyboardNav.js). Returns the cell the
  // keyboard moved to, for the grids to scroll into view.
  const gridCursorKey = useDbGridKeyboardNav({
    enabled: !!result && !loading,
    editing: !!editingCell,
    writable: !isQueryTab && !loading && !error,
    transposed,
    visualRows,
    drawnColIndexes,
    recordRowIndexes: transposed ? Array.from(selectedRows).sort((a, b) => a - b) : null,
    selectedCells,
    setSelectedCells,
    anchor: cellAnchorRef.current,
    setCellAnchor,
    openCell: (row, col) => handleCellOpen(row, col, result?.rows?.[row]?.[col]),
    typeIntoCell,
    typeIntoPendingCell,
    applyCellWrites,
  });

  return {
    toggleRowSelected,
    visualRows,
    find,
    findMatchKeys,
    activeFindKey,
    findPosRef,
    stepFind,
    closeFind,
    toggleCellSelected,
    applyCellWrites,
    gridCursorKey,
  };
}
